import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, SettingsSort18 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { IconButton, Sizes, Variants } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { HabitGroupBy, HabitSortingMode } from 'shared/types/sorting-mode';
import { useTheme } from 'styled-components';

import { groupByOptions, sortOptions } from './sort-options';

export type HeaderSortButtonProps = {
  sort?: HabitSortingMode;
  groupBy?: HabitGroupBy;
  onSort: (sort: HabitSortingMode) => void;
  onGroupBy: (groupBy: HabitGroupBy) => void;
};

export const HeaderSortButton: React.FC<HeaderSortButtonProps> = ({
  sort,
  groupBy,
  onSort,
  onGroupBy,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const { t } = useTranslation();

  const _onGroupBy = (value: HabitGroupBy) => {
    onGroupBy(value);
    closeMenu();
  };

  const _onSort = (value: HabitSortingMode) => {
    onSort(value);
    closeMenu();
  };

  return (
    <>
      <IconButton
        ref={containerRef}
        icon={SettingsSort18}
        size={Sizes.Medium}
        onClick={openMenu}
        variant={theme.isMobile ? Variants.Outlined : Variants.Contained}
        preventDrag
      />

      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          position="bottom-end"
          onOutside={closeMenu}
        >
          <PopupMenuList hasBottomBorder>
            <PopupMenuTitle title={t('pages.habits.group-by.title')} />

            {groupByOptions.map(({ id, label, value }) => (
              <PopupMenuListItem key={id}>
                <PopupMenuButton
                  start={groupBy === id ? <Icon icon={Check} /> : undefined}
                  onClick={() => _onGroupBy(value)}
                  indentationLevel={groupBy !== id ? 1 : 0}
                >
                  {t(label)}
                </PopupMenuButton>
              </PopupMenuListItem>
            ))}
          </PopupMenuList>
          <PopupMenuList>
            <PopupMenuTitle title={t('general.labels.habits.sort.title')} />

            {sortOptions.map(({ id, label, value }) => (
              <PopupMenuListItem key={id}>
                <PopupMenuButton
                  start={sort === id ? <Icon icon={Check} /> : undefined}
                  onClick={() => _onSort(value)}
                  indentationLevel={sort !== id ? 1 : 0}
                >
                  {t(label)}
                </PopupMenuButton>
              </PopupMenuListItem>
            ))}
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
